import { faRepeat } from "@fortawesome/pro-solid-svg-icons";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { isApiError } from "~/api/error";
import { Button, DialogError, Field } from "~/components";
import { useImpersonateTill } from "~/hooks/queries/auth";
import { useDeviceData } from "~/providers/app";
import { useAuth } from "~/providers/store/auth";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

type SwitchTillInputs = {
  tillId: string;
};

export const AdminPage = () => {
  const t = useT();

  const { deviceTillId, isKanplaAdmin } = useAuth();
  const { deviceId } = useDeviceData();
  const {
    mutate: impersonateTill,
    status: impersonateStatus,
    error: impersonateError
  } = useImpersonateTill();

  const { data: currentTill } = useCurrentTill();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SwitchTillInputs>();

  if (!isKanplaAdmin) {
    return <Navigate replace to="/profiles" />;
  }

  if (impersonateStatus === "error") {
    const errorText =
      isApiError(impersonateError) && impersonateError.status === 404 ? (
        <T _str="Till has been deleted" />
      ) : (
        <T _str="Something went wrong, you can try logging in again" />
      );

    return (
      <DialogError error={errorText} hideSupport>
        <Button
          onClick={() => {
            location.reload();
          }}
          variant="danger"
        >
          <T _str="Reload" />
        </Button>
      </DialogError>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-10 bg-interface-neutral-6">
      <h1 className="text-center text-3xl font-bold text-white">
        <T _str="Kanpla POS Admin" />
      </h1>

      <div className="w-full max-w-md space-y-8">
        <div className="rounded-xl border border-interface-neutral-4/30 bg-interface-neutral-5/40 p-6 shadow-lg backdrop-blur-sm">
          {currentTill ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="size-3 animate-pulse rounded-full bg-interface-positive-2" />
                <span className="text-sm text-interface-neutral-2">
                  <T _str="Active Till" />
                </span>
              </div>
              <div className="rounded-lg border border-interface-positive-2/30 bg-interface-positive-3/20 p-4">
                <p className="font-medium text-white">
                  <T _str="Name: {tillName}" tillName={currentTill.name} />
                </p>
                <p className="mt-1 text-sm text-interface-neutral-2">
                  <T _str="ID: {tillId}" tillId={currentTill.id} />
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="size-3 rounded-full bg-interface-neutral-4" />
                <span className="text-sm text-interface-neutral-2">
                  <T _str="No Active Till" />
                </span>
              </div>
              <div className="rounded-lg border border-interface-neutral-4/30 bg-interface-neutral-5/40 p-4">
                <p className="text-interface-neutral-2">
                  <T _str="No till is currently active on this device" />
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-interface-neutral-4/30 bg-interface-neutral-5/40 p-6 shadow-lg backdrop-blur-sm">
          <form
            className="space-y-6"
            onSubmit={handleSubmit(({ tillId }) => {
              impersonateTill({ tillId });
            })}
          >
            <Field
              error={errors.tillId?.message}
              label="Till ID"
              labelClassName="text-white !font-semibold"
              placeholder="Enter Till ID"
              {...register("tillId", { required: t("Till ID is required") })}
            />

            <div className="flex flex-col gap-3">
              <Button
                icon={faRepeat}
                loading={impersonateStatus === "pending"}
                size="large"
                type="submit"
                variant="primary"
              >
                <T _str="Mask till" />
              </Button>

              <Button
                hidden={!deviceTillId || !deviceId}
                loading={impersonateStatus === "pending"}
                onClick={() => {
                  if (!deviceTillId) throw new Error("DeviceTillId is missing");
                  if (!deviceId) throw new Error("Device ID is missing");

                  impersonateTill({ tillId: deviceTillId, deviceId });
                }}
                size="large"
                variant="info"
              >
                <T _str="Switch to device till" />
              </Button>
            </div>
          </form>
        </div>

        <div className="flex justify-center">
          <Link to="/profiles">
            <Button
              disabled={impersonateStatus === "pending"}
              size="medium"
              variant="secondary"
            >
              <T _str="Back to Profiles" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
